import { useEffect } from "react";
import { HERO_IMG } from "@/data/shop";

export const SITE_TITLE = "ShopaBee | Discover Something Worth Buying";
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");
export const DEFAULT_DESCRIPTION = "ShopaBee is a UK online shop for new, open-box, pre-loved and clearance finds, with every condition clearly described and prices in pounds.";

export const slugify = (s = "") => s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

// Readable slug plus a short id suffix keeps addresses unique; bare ids still resolve.
export const productSlug = (p) => `${slugify(p.name) || "item"}-${String(p.id).slice(0, 8)}`;
export const productPath = (p) => `/product/${productSlug(p)}`;
export const categoryPath = (id) => `/shop/${id}`;
export const findProduct = (products, param) => products.find((x) => x.id === param || productSlug(x) === param);
export const absUrl = (path = "/") => (/^https?:/.test(path) ? path : `${SITE_URL}${path}`);

// Only the real domain is indexable; preview/staging hosts are always noindex.
const indexableHost = () => !!SITE_URL && window.location.origin === SITE_URL;

const setTag = (selector, create, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement(create); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};
const meta = (key, content, attr = "name") => setTag(`meta[${attr}="${key}"]`, "meta", { [attr]: key, content });

export const clip = (s = "", n = 158) => {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length <= n ? t : `${t.slice(0, n - 1).replace(/\s+\S*$/, "").replace(/[\s,;:–—-]+$/, "")}…`;
};

export const useSeo = ({ title, description, path, image, type = "website", noindex = false, jsonLd }) => {
  const ld = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const full = !title ? SITE_TITLE : title.includes("ShopaBee") ? title : `${title} | ShopaBee`;
    const desc = clip(description || DEFAULT_DESCRIPTION);
    const url = absUrl(path || window.location.pathname);
    const img = absUrl(image || HERO_IMG);
    document.title = full;
    meta("description", desc);
    meta("robots", noindex || !indexableHost() ? "noindex, follow" : "index, follow");
    setTag('link[rel="canonical"]', "link", { rel: "canonical", href: url });
    [["og:title", full], ["og:description", desc], ["og:url", url], ["og:image", img], ["og:type", type], ["og:site_name", "ShopaBee"], ["og:locale", "en_GB"]]
      .forEach(([k, v]) => meta(k, v, "property"));
    [["twitter:card", "summary_large_image"], ["twitter:title", full], ["twitter:description", desc], ["twitter:image", img]].forEach(([k, v]) => meta(k, v));
    const old = document.getElementById("page-jsonld");
    if (old) old.remove();
    if (ld) {
      const s = document.createElement("script");
      s.type = "application/ld+json"; s.id = "page-jsonld"; s.text = ld;
      document.head.appendChild(s);
    }
  }, [title, description, path, image, type, noindex, ld]);
};

export const useTitle = (title) => useSeo({ title, noindex: true });

export const breadcrumbLd = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: absUrl(c.path) })),
});
